/**
 * Partial accessorial builder
 */

import { AccessorialType } from '../../constants/accessorial-types.js'
import type { Accessorial } from '../../types/accessorial.js'
import type { BuildPartialOptions } from './types.js'
import { calcPartialPay } from '../shared/calculations.js'
import { buildAccessorial } from '../shared/buildAccessorial.js'

/**
 * Build Partial Accessorial
 *
 * Used when a move is partially completed (cancelled mid-route).
 * The customer and driver are charged/paid based on how far the move progressed.
 *
 * Cost Responsibility Breakdown:
 * - HopDrive: No direct cost (always 0)
 * - Customer: Pays cancellation fee if responsible
 * - Driver: Paid fraction of move pay based on progress
 *
 * @param options - Partial accessorial options
 * @returns Insertable partial accessorial object
 * @throws Error if validation fails
 *
 * @example
 * ```typescript
 * const partialAccessorial = buildPartialAccessorial({
 *   moveId: 12345,
 *   customerResponsible: true,
 *   moveStatus: 'pickup successful',
 *   movePay: 50.00,
 *   cancellationFee: 25.00
 * })
 * // Result: { cost: 0, ar_amount: 25.00, ap_amount: 25.00, ... }
 * // (Driver gets 50% pay for reaching 'pickup successful' status)
 * ```
 */
export function buildPartialAccessorial(options: BuildPartialOptions): Accessorial {
  // Validation for required props
  if (!options || typeof options !== 'object') {
    throw new Error('Failed to build partial accessorial: Invalid options prop!')
  }
  if (!options.moveId || (typeof options.moveId !== 'number' && typeof options.moveId !== 'bigint')) {
    throw new Error('Failed to build partial accessorial: Invalid moveId prop!')
  }
  if (typeof options.customerResponsible !== 'boolean') {
    throw new Error('Failed to build partial accessorial: Invalid customerResponsible prop!')
  }
  if (typeof options.moveStatus !== 'string') {
    throw new Error('Failed to build partial accessorial: Invalid moveStatus prop!')
  }
  if (typeof options.movePay !== 'number') {
    throw new Error('Failed to build partial accessorial: Invalid movePay prop!')
  }
  if (typeof options.cancellationFee !== 'number') {
    throw new Error('Failed to build partial accessorial: Invalid cancellationFee prop!')
  }
  if (options.notes !== undefined && options.notes !== null && typeof options.notes !== 'string') {
    throw new Error('Failed to build partial accessorial: Invalid notes prop!')
  }

  // Calculate partial price to customer (if customer is responsible)
  const partialPrice = options.customerResponsible ? options.cancellationFee : 0

  // Calculate partial pay to driver based on move progress
  const partialPay = calcPartialPay(options.moveStatus, options.movePay)

  // Build accessorial using base builder
  return buildAccessorial({
    moveId: options.moveId,
    code: AccessorialType.PARTIAL.code,
    cost: 0,
    ar_amount: partialPrice,
    ap_amount: partialPay,
    notes: options.notes || AccessorialType.PARTIAL.desc,
  })
}
